import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import uniq from 'lodash/uniq';

// Hooks
import useWindowDimensions from 'Wpds/hooks/useWindowDimensions';
import { useMembers } from 'Shared/utils/hooks';

// Components
import { WpContainer, WpContainerBody, WpContainerHeader } from 'Wpds/components/container';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import { WpMenu, WpMenuItem } from 'Wpds/components/menu';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import Spinner from 'Shared/components/Spinner';
import BarChart from 'Shared/components/charts/BarChart';
import KeyEngagementActivities from './KeyEngagementActivities';
import ExecutiveWorkspaceActivity from './ExecutiveWorkspaceActivity';
import ExecutiveCoachingSummary from './ExecutiveCoachingSummary';

// API
import { getExecutiveDashboardV2 } from 'Shared/api/Dashboards.api';

// Utils
import { trackUsedAdminDashFilter, trackViewedAdminDashMetric } from 'Shared/tracking/tracking';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Styles
import './ExecutiveDashboardV2.scss';

const LAST_3_MONTHS = 'last_3_months';
const LAST_6_MONTHS = 'last_6_months';
const LAST_YEAR = 'last_year';
const ALL_TIME = 'all_time';
const DATE_RANGES = {
  [LAST_3_MONTHS]: 'Last 3 Months',
  [LAST_6_MONTHS]: 'Last 6 Months',
  [LAST_YEAR]: 'Last Year',
  [ALL_TIME]: 'All time',
};

const SUM_OF_NUMBER_OF_ACTIVITIES_CUMULATIVE = 'organization_activity';
const KEY_ENGAGEMENT_ACTIVITIES = 'key_engagement_activities';
const MEETING_SUMMARY = 'meeting_summary';
const WORKSPACE_ACTIVITIES = 'workspace_activity';
const COACHING_SUMMARY = 'coaching_activity';
const METRICS = {
  [SUM_OF_NUMBER_OF_ACTIVITIES_CUMULATIVE]: 'Activity Summary',
  [KEY_ENGAGEMENT_ACTIVITIES]: 'Engagement Scorecard',
  [MEETING_SUMMARY]: 'Meeting Summary',
  [WORKSPACE_ACTIVITIES]: 'Workspace Activity',
  [COACHING_SUMMARY]: 'Feedback Summary',
};

const ALL_DEPARTMENTS_FILTER = 'All';

const ExecutiveDashboardV2 = () => {
  const { width: windowWidth } = useWindowDimensions();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(LAST_6_MONTHS);
  const [selectedDepartment, setSelectedDepartment] = useState(ALL_DEPARTMENTS_FILTER);
  const [metric, setMetric] = useState(SUM_OF_NUMBER_OF_ACTIVITIES_CUMULATIVE);
  const showAsColumn = windowWidth < 1106;
  const { isLoading: isLoadingMembers } = useMembers({ refresh: true });

  const members = useSelector(state => state.members);
  const departments = isLoadingMembers
    ? []
    : uniq(members.filter(member => member.department).map(member => member.department));

  const metricToolTip =
    {
      [SUM_OF_NUMBER_OF_ACTIVITIES_CUMULATIVE]: 'The total number of activities users in your organization have done',
      [KEY_ENGAGEMENT_ACTIVITIES]: 'A scorecard of what activities users in your organization are engaged with',
      [MEETING_SUMMARY]: 'How many meetings users in your organization have had in Perform',
      [WORKSPACE_ACTIVITIES]: 'A summary of in-workspace activity for users in your organization',
      [COACHING_SUMMARY]: 'A summary of coaching and goal-setting behaviors for users in your organization',
    }[metric] || 'Unknown Metric';

  useEffect(() => {
    setLoading(true);
    getExecutiveDashboardV2({ dateRange, chart: metric, department: selectedDepartment }).then(response => {
      setDashboardData(response.data);
      setLoading(false);
    });
  }, [dateRange, metric, selectedDepartment]);

  useEffect(() => {
    trackUsedAdminDashFilter(TrackingConstants.ADMIN_DASH_FILTER_TYPES.DATE_RANGE);
  }, [dateRange]);

  useEffect(() => {
    if (selectedDepartment !== ALL_DEPARTMENTS_FILTER) {
      trackUsedAdminDashFilter(TrackingConstants.ADMIN_DASH_FILTER_TYPES.DEPARTMENT);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    trackViewedAdminDashMetric(metric);
  }, [metric]);

  const baseClass = 'ExecutiveDashboardV2';
  const classes = classnames(baseClass, showAsColumn && `${baseClass}--column`);

  return (
    <div className={classes}>
      <div className={`${baseClass}__menu-wrapper`}>
        <div className={`${baseClass}__menu-wrapper__menu`}>
          <WpMenu
            onMenuItemClick={setMetric}
            activeItemId={metric}
            direction={showAsColumn ? 'horizontal' : 'vertical'}
          >
            <WpMenuItem menuItemId={SUM_OF_NUMBER_OF_ACTIVITIES_CUMULATIVE}>
              {METRICS[SUM_OF_NUMBER_OF_ACTIVITIES_CUMULATIVE]}
            </WpMenuItem>
            <WpMenuItem menuItemId={KEY_ENGAGEMENT_ACTIVITIES}>{METRICS[KEY_ENGAGEMENT_ACTIVITIES]}</WpMenuItem>
            <WpMenuItem menuItemId={MEETING_SUMMARY}>{METRICS[MEETING_SUMMARY]}</WpMenuItem>
            <WpMenuItem menuItemId={WORKSPACE_ACTIVITIES}>{METRICS[WORKSPACE_ACTIVITIES]}</WpMenuItem>
            <WpMenuItem menuItemId={COACHING_SUMMARY}>{METRICS[COACHING_SUMMARY]}</WpMenuItem>
          </WpMenu>
        </div>
      </div>
      <div className={`${baseClass}__body`}>
        <div className={`${baseClass}__controls`}>
          <div className="d-flex align-items-center">
            <span className={`${baseClass}__controls__date_filter`}>Date Range: </span>
            <WpDropdown placement="bottom-end">
              <WpDropdownToggle>
                <WpButton color="primary" variant="tertiary" size="sm">
                  {DATE_RANGES[dateRange]}
                  <WpIcon name="angle-down" />
                </WpButton>
              </WpDropdownToggle>
              <WpDropdownMenu>
                <WpDropdownMenuItem onClick={() => setDateRange(LAST_3_MONTHS)}>
                  {DATE_RANGES[LAST_3_MONTHS]}
                </WpDropdownMenuItem>
                <WpDropdownMenuItem onClick={() => setDateRange(LAST_6_MONTHS)}>
                  {DATE_RANGES[LAST_6_MONTHS]}
                </WpDropdownMenuItem>
                <WpDropdownMenuItem onClick={() => setDateRange(LAST_YEAR)}>
                  {DATE_RANGES[LAST_YEAR]}
                </WpDropdownMenuItem>
                <WpDropdownMenuItem onClick={() => setDateRange(ALL_TIME)}>{DATE_RANGES[ALL_TIME]}</WpDropdownMenuItem>
              </WpDropdownMenu>
            </WpDropdown>
            <span className={`${baseClass}__controls__department_filter`}>Department: </span>
            <WpDropdown placement="bottom-end">
              <WpDropdownToggle>
                <WpButton color="primary" variant="tertiary" size="sm">
                  {selectedDepartment}
                  <WpIcon name="angle-down" />
                </WpButton>
              </WpDropdownToggle>
              <WpDropdownMenu>
                <WpDropdownMenuItem onClick={() => setSelectedDepartment(ALL_DEPARTMENTS_FILTER)}>
                  {ALL_DEPARTMENTS_FILTER}
                </WpDropdownMenuItem>
                ;
                {departments.map(dept => (
                  <WpDropdownMenuItem onClick={() => setSelectedDepartment(dept)}>{dept}</WpDropdownMenuItem>
                ))}
              </WpDropdownMenu>
            </WpDropdown>
          </div>
        </div>

        {loading && <Spinner activeIf />}
        {!loading && (
          <WpContainer>
            <WpContainerHeader>
              <h5>{METRICS[metric]}</h5>
              <span className={`${baseClass}__sub-title`}>{metricToolTip}</span>
            </WpContainerHeader>
            <WpContainerBody padding={0}>
              {metric === SUM_OF_NUMBER_OF_ACTIVITIES_CUMULATIVE && dashboardData.organization_activity && (
                <div className={`${baseClass}__chart`}>
                  <BarChart
                    datasets={dashboardData.organization_activity.chart.datasets}
                    labels={dashboardData.organization_activity.chart.labels}
                    options={dashboardData.organization_activity.chart.options}
                  />
                </div>
              )}
              {metric === KEY_ENGAGEMENT_ACTIVITIES && dashboardData.key_engagement_activities && (
                <KeyEngagementActivities keyEngagementActivities={dashboardData.key_engagement_activities} />
              )}

              {metric === MEETING_SUMMARY && dashboardData.meeting_summary && (
                <div className={`${baseClass}__chart`}>
                  <BarChart
                    datasets={dashboardData.meeting_summary.chart.datasets}
                    labels={dashboardData.meeting_summary.chart.labels}
                    options={dashboardData.meeting_summary.chart.options}
                  />
                </div>
              )}
              {metric === WORKSPACE_ACTIVITIES && (
                <ExecutiveWorkspaceActivity workspaceActivity={dashboardData.workspace_activity} />
              )}
              {metric === COACHING_SUMMARY && (
                <ExecutiveCoachingSummary coachingSummary={dashboardData.coaching_activity} />
              )}
            </WpContainerBody>
          </WpContainer>
        )}
      </div>
    </div>
  );
};

export default ExecutiveDashboardV2;
